<template>
  <div class="study-page">

    <header class="study-header">
      <div class="header-text">
        <h2 class="page-title">学习数据分析</h2>
        <p class="page-subtitle">分析您的学习行为,助力职业成长</p>
      </div>

    </header>


    <section class="study-stats-grid">

      <div class="study-stat-card">
        <div class="study-stat-icon blue">
          <i class="el-icon-date"></i>
        </div>
        <div class="study-stat-content">
          <p class="study-stat-label">{{ hoursLabel }}</p>
          <div class="study-stat-value-row">
            <span class="study-stat-value">{{ studyData.hours }}</span>
            <span class="study-stat-unit">小时</span>
          </div>
          <div class="study-stat-trend up">
            <i class="el-icon-sort-up"></i>
            {{ studyData.hoursGrowth }}% {{ growthLabel }}</div>
        </div>
      </div>


      <div class="study-stat-card">
        <div class="study-stat-icon purple">
          <i class="el-icon-video-play"></i>
        </div>
        <div class="study-stat-content">
          <p class="study-stat-label">{{ videosLabel }}</p>
          <div class="study-stat-value-row">
            <span class="study-stat-value">{{ studyData.videosWatched }}</span>
            <span class="study-stat-unit">个</span>
          </div>
          <div class="study-stat-trend up">
            <i class="el-icon-sort-up"></i>
            {{ studyData.videosGrowth }}% {{ growthLabel }}</div>
        </div>
      </div>

      <div class="study-stat-card">
        <div class="study-stat-icon green">
          <i class="el-icon-success"></i>
        </div>
        <div class="study-stat-content">
          <p class="study-stat-label">{{ completionLabel }}</p>
          <div class="study-stat-value-row">
            <span class="study-stat-value">{{ studyData.completionRate }}</span>
            <span class="study-stat-unit">%</span>
          </div>
          <div class="progress-bar-mini">
            <div class="progress-fill" :style="{ width: studyData.completionRate + '%' }"></div>
          </div>
        </div>
      </div>
    </section>


    <section class="chart-section">
      <div class="chart-header">
        <h3 class="chart-title">学习时长趋势 (近30天)</h3>
        <div class="chart-legend">
          <div class="legend-item">
            <div class="legend-color"></div>
            每日学习时长
          </div>
        </div>
      </div>
      <div class="chart-wrapper">
        <div class="chart-grid-bg"></div>
        <svg class="chart-svg" viewBox="0 0 1000 200" preserveAspectRatio="none">
          <defs>
            <linearGradient id="chartGradient" x1="0%" x2="0%" y1="0%" y2="100%">
              <stop offset="0%" stop-color="#1a71ff" />
              <stop offset="100%" stop-color="#1a71ff" stop-opacity="0" />
            </linearGradient>
          </defs>
          <path :d="chartPath"
                fill="none"
                stroke="#1a71ff"
                stroke-width="3" />
          <path :d="chartAreaPath"
                fill="url(#chartGradient)"
                opacity="0.1" />
        </svg>
        <div class="chart-labels">
          <span v-for="(label, index) in chartLabels" :key="index">{{ label }}</span>
        </div>
      </div>
    </section>


    <div class="study-bottom-grid">

      <section class="distribution-section">
        <h3 class="section-title">视频分类分布</h3>
        <div class="distribution-content">
          <div class="donut-chart">
            <svg viewBox="0 0 32 32" class="donut-svg">
              <circle cx="16" cy="16" r="13" fill="transparent" stroke="#eff6ff" stroke-width="6" />
              <circle v-for="(segment, index) in distributionSegments"
                      :key="index"
                      cx="16"
                      cy="16"
                      r="13"
                      fill="transparent"
                      :stroke="segment.color"
                      stroke-width="6"
                      :stroke-dasharray="segment.dasharray"
                      :stroke-dashoffset="segment.dashoffset" />
            </svg>
            <div class="donut-center">
              <span class="donut-value">{{ distributionTotalPercent }}%</span>
              <span class="donut-label">总占比</span>
            </div>
          </div>
          <div class="distribution-list">
            <div v-for="(item, index) in distributionItems" :key="index" class="distribution-item">
              <div class="distribution-label">
                <div class="distribution-dot" :style="{ background: item.color }"></div>
                {{ item.name }}
              </div>
              <span class="distribution-value">{{ item.percent }}%</span>
            </div>
          </div>
        </div>
      </section>

      <section class="progress-section">
        <h3 class="section-title">部门视频学习进度</h3>
        <div class="progress-list">
          <div v-for="(course, index) in departmentCourses" :key="index" class="progress-item">
            <div class="progress-item-header">
              <div class="progress-item-info">
                <h4 class="progress-item-title">{{ course.name }}</h4>
                <p v-if="course.deadline" class="progress-item-date">截止日期: {{ course.deadline }}</p>
              </div>
              <span class="progress-item-percent" :class="{ completed: course.progress === 100 }">
                {{ course.progress }}%
              </span>
            </div>
            <div class="progress-bar">
              <div class="progress-bar-fill"
                   :style="{ width: course.progress + '%', backgroundColor: course.color }">
              </div>
            </div>
          </div>
        </div>
        <button class="view-all-btn" @click="handleViewAllCourses">
          查看全部必修课程
          <i class="el-icon-arrow-right"></i>
        </button>
      </section>
    </div>
  </div>
</template>

<script>
import { getStudyDashboard } from '@/api/api'

const DISTRIBUTION_COLORS = ['#1a71ff', '#8b5cf6', '#cbd5e1', '#10b981']
const COURSE_COLORS = ['#1a71ff', '#10b981', '#60a5fa', '#cbd5e1', '#f59e0b', '#f97316']
const DONUT_RADIUS = 13
const DONUT_CIRCUMFERENCE = 2 * Math.PI * DONUT_RADIUS

export default {
  name: 'Study',
  data() {
    return {
      rangeMode: 'monthly',
      studyData: {
        hours: 0,
        hoursGrowth: 0,
        videosWatched: 0,
        videosGrowth: 0,
        completionRate: 0
      },
      chartLabels: [],
      chartPath: '',
      chartAreaPath: '',
      distributionItems: [],
      distributionSegments: [],
      distributionTotalPercent: 0,
      departmentCourses: []
    }
  },
  computed: {
    hoursLabel() {
      return this.rangeMode === 'rolling30' ? '近30天学习时长' : '本月学习时长'
    },
    videosLabel() {
      return this.rangeMode === 'rolling30' ? '近30天观看视频数' : '已观看视频数'
    },
    completionLabel() {
      return this.rangeMode === 'rolling30' ? '近30天完成率' : '学习完成率'
    },
    growthLabel() {
      return this.rangeMode === 'rolling30' ? '较前30天' : '较上月'
    }
  },
  async created() {
    await this.loadStudyDashboard()
  },
  methods: {
    async loadStudyDashboard() {
      try {
        const res = await getStudyDashboard({ rangeMode: this.rangeMode })
        if (!(res.code === 1000 || res.success === true)) {
          this.applyDashboardFallback()
          return
        }

        const data = res.data || {}
        if (data.rangeMode) {
          this.rangeMode = data.rangeMode
        }

        const stats = data.stats || {}
        this.studyData = {
          hours: Number(stats.hours || 0),
          hoursGrowth: Number(stats.hoursGrowth || 0),
          videosWatched: Number(stats.videosWatched || 0),
          videosGrowth: Number(stats.videosGrowth || 0),
          completionRate: Number(stats.completionRate || 0)
        }

        this.applyTrend(Array.isArray(data.trend) ? data.trend : [])
        this.applyDistribution(Array.isArray(data.distribution) ? data.distribution : [])
        this.applyCourses(Array.isArray(data.courses) ? data.courses : [])
      } catch (error) {
        console.error('Failed to load study dashboard:', error)
        this.applyDashboardFallback()
      }
    },

    applyDashboardFallback() {
      this.studyData = {
        hours: 0,
        hoursGrowth: 0,
        videosWatched: 0,
        videosGrowth: 0,
        completionRate: 0
      }
      this.applyTrend([])
      this.applyDistribution([])
      this.applyCourses([])
    },

    applyTrend(series) {
      const safeSeries = Array.isArray(series) ? series : []
      this.chartLabels = this.buildTrendLabels(safeSeries)
      const paths = this.buildChartPaths(safeSeries.map(item => Number(item.value || 0)))
      this.chartPath = paths.line
      this.chartAreaPath = paths.area
    },

    buildTrendLabels(series) {
      if (!series.length) {
        return []
      }
      const labelCount = 7
      const labels = []
      for (let i = 0; i < labelCount; i += 1) {
        const index = Math.round((i / (labelCount - 1)) * (series.length - 1))
        labels.push(series[index].label)
      }
      return labels
    },

    buildChartPaths(values) {
      const width = 1000
      const height = 200
      const top = 20
      const bottom = 180
      const range = bottom - top

      if (!values.length) {
        return {
          line: `M 0 ${bottom} L ${width} ${bottom}`,
          area: `M 0 ${bottom} L ${width} ${bottom} L ${width} ${height} L 0 ${height} Z`
        }
      }

      const maxValue = Math.max(...values, 0)
      const points = values.map((value, index) => {
        const x = values.length === 1 ? 0 : (index / (values.length - 1)) * width
        const y = maxValue > 0 ? bottom - (value / maxValue) * range : bottom
        return { x, y }
      })

      const line = points
        .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`)
        .join(' ')
      const area = `${line} L ${width} ${height} L 0 ${height} Z`

      return { line, area }
    },

    applyDistribution(items) {
      if (!items.length) {
        this.distributionItems = [{ name: '暂无数据', percent: 0, color: '#cbd5e1' }]
        this.distributionSegments = []
        this.distributionTotalPercent = 0
        return
      }

      const mapped = items.map((item, index) => {
        const percent = Math.max(0, Math.min(100, Number(item.percent || 0)))
        return {
          name: item.name || '未分类',
          percent,
          color: DISTRIBUTION_COLORS[index % DISTRIBUTION_COLORS.length]
        }
      })

      const percentSum = mapped.reduce((sum, item) => sum + item.percent, 0)
      if (mapped.length > 0 && percentSum !== 100) {
        mapped[mapped.length - 1].percent = Math.max(0, mapped[mapped.length - 1].percent + (100 - percentSum))
      }

      this.distributionItems = mapped
      this.distributionTotalPercent = mapped.reduce((sum, item) => sum + item.percent, 0)

      let offset = 0
      this.distributionSegments = mapped
        .filter(item => item.percent > 0)
        .map(item => {
          const length = (item.percent / 100) * DONUT_CIRCUMFERENCE
          const segment = {
            ...item,
            dasharray: `${length} ${DONUT_CIRCUMFERENCE - length}`,
            dashoffset: -offset
          }
          offset += length
          return segment
        })
    },

    applyCourses(courses) {
      this.departmentCourses = (courses || []).map((course, index) => {
        const progress = Math.max(0, Math.min(100, Number(course.progress || 0)))
        return {
          name: course.name || '未命名课程',
          deadline: course.deadline || '',
          progress,
          color: COURSE_COLORS[index % COURSE_COLORS.length]
        }
      })
    },


    handleViewAllCourses() {
      this.$router.push({ path: '/departmentVideo' })
    }
  }
}
</script>

<style scoped>
.study-page {
  width: 100%;
}


.study-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 32px;
}

.header-text {
  flex: 1;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 4px 0;
}

.page-subtitle {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.action-btn.secondary {
  background: white;
  color: #0f172a;
  border: 1px solid #e2e8f0;
}

.action-btn.secondary:hover {
  background: #f8fafc;
}

.action-btn.primary {
  background: #1a71ff;
  color: white;
}

.action-btn.primary:hover {
  background: #1557d0;
}


/* 椤堕儴缁熻鍗＄墖 */
.study-stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
}

.study-stat-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  gap: 20px;
}

.study-stat-icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.study-stat-icon.blue {
  background: rgba(59, 130, 246, 0.05);
  color: #3b82f6;
}

.study-stat-icon.purple {
  background: rgba(168, 85, 247, 0.05);
  color: #a855f7;
}

.study-stat-icon.green {
  background: rgba(16, 185, 129, 0.05);
  color: #10b981;
}


.study-stat-content {
  flex: 1;
}

.study-stat-label {
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  margin: 0 0 4px 0;
}

.study-stat-value-row {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 8px;
}

.study-stat-value {
  font-size: 24px;
  font-weight: 700;
  color: #0f172a;
}

.study-stat-unit {
  font-size: 14px;
  font-weight: 500;
  color: #94a3b8;
}

.study-stat-trend {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 700;
}

.study-stat-trend.up {
  color: #52c41a;
}


.progress-bar-mini {
  width: 96px;
  height: 6px;
  background: #f1f5f9;
  border-radius: 999px;
  overflow: hidden;
  margin-top: 8px;
}

.progress-fill {
  height: 100%;
  background: #10b981;
  border-radius: 999px;
  transition: width 0.3s;
}

/* 瀛︿範鏃堕暱瓒嬪娍鍥?*/
.chart-section {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  margin-bottom: 32px;
}

.chart-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 32px;
}

.chart-title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.chart-legend {
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
}

.chart-legend .legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.chart-legend .legend-color {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #1a71ff;
}

.chart-wrapper {
  position: relative;
  height: 256px;
  width: 100%;
}

.chart-grid-bg {
  position: absolute;
  inset: 0;
  background-image: linear-gradient(to right, #e2e8f0 1px, transparent 1px),
                    linear-gradient(to bottom, #e2e8f0 1px, transparent 1px);
  background-size: 40px 40px;
  opacity: 0.2;
}

.chart-svg {
  position: relative;
  width: 100%;
  height: 100%;
}

.chart-labels {
  position: absolute;
  bottom: -24px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

/* 搴曢儴鍙屾爮 */
.study-bottom-grid {
  display: grid;
  grid-template-columns: 2fr 3fr;
  gap: 32px;
}

/* 瑙嗛鍒嗙被鍒嗗竷 */
.distribution-section {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.distribution-section .section-title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 32px 0;
}

.distribution-content {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.donut-chart {
  position: relative;
  width: 192px;
  height: 192px;
  margin-bottom: 32px;
}

.donut-svg {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}

.donut-center {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.donut-value {
  font-size: 30px;
  font-weight: 800;
  color: #0f172a;
}

.donut-label {
  font-size: 10px;
  font-weight: 700;
  color: #94a3b8;
}

.distribution-list {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.distribution-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.distribution-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
}

.distribution-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.distribution-dot.blue {
  background: #1a71ff;
}

.distribution-dot.purple {
  background: #8b5cf6;
}

.distribution-dot.gray {
  background: #cbd5e1;
}

.distribution-value {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}

/* 閮ㄩ棬瑙嗛瀛︿範杩涘害 */
.progress-section {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.progress-section .section-title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 32px 0;
}

.progress-list {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.progress-item-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 8px;
}

.progress-item-info {
  flex: 1;
}

.progress-item-title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 4px 0;
}

.progress-item-date {
  font-size: 12px;
  color: #64748b;
  margin: 0;
}

.progress-item-percent {
  font-size: 14px;
  font-weight: 700;
  color: #64748b;
}

.progress-item-percent.completed {
  color: #10b981;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: #f1f5f9;
  border-radius: 999px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.3s;
}

.view-all-btn {
  width: 100%;
  margin-top: 32px;
  padding: 10px 16px;
  background: transparent;
  border: none;
  border-top: 1px solid #f1f5f9;
  padding-top: 24px;
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  transition: color 0.2s;
}

.view-all-btn:hover {
  color: #1a71ff;
}


@media (max-width: 768px) {
  .study-header {
    flex-direction: column;
  }

  .header-actions {
    width: 100%;
    flex-direction: column;
  }

  .action-btn {
    width: 100%;
    justify-content: center;
  }

  .study-stats-grid {
    grid-template-columns: 1fr;
  }

  .study-bottom-grid {
    grid-template-columns: 1fr;
  }
}
</style>





